import React, { useCallback, useEffect, useRef, useState } from 'react';
import { ArrowLeft, Clock3, FileAudio, FolderOpen, LoaderCircle, LockKeyhole, Mic, RefreshCw, RotateCcw, Settings, Sparkles } from 'lucide-react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import {
  meetlyApi,
  normalizeMeetlyMeetingStatus,
  type MeetlyMeeting,
  type MeetlyMeetingStatusRaw,
} from '../../api/meetly';
import MeetlyRecorder, { type StoppedRecording } from '../../components/MeetlyRecorder';
import MeetlyResultWorkspace from '../../components/MeetlyResultWorkspace';
import MeetlyMeetingList from './MeetlyMeetingList';

const isPending = (status: MeetlyMeetingStatusRaw) => {
  const normalizedStatus = normalizeMeetlyMeetingStatus(status);
  return normalizedStatus === 'SUBMITTING' || normalizedStatus === 'QUEUED';
};
const isCaptureRequest = (search: string) => new URLSearchParams(search).get('record') === '1';
const PENDING_MEETING_REFRESH_MS = 3_000;

const submissionErrorMessage = (error: unknown) => {
  const status = (error as { response?: { status?: number } })?.response?.status;
  if (status === 429) {
    return 'No meeting credits are available this month. Ask an administrator to increase your Meetly allowance.';
  }
  if (status === 503) {
    return 'Meetly processing is not configured on this TeamLio server. Ask an administrator to complete the private Meetly setup.';
  }
  return 'Meetly could not submit this meeting. Keep this page open and try again.';
};

const stepFor = (status: MeetlyMeetingStatusRaw) => {
  const normalizedStatus = normalizeMeetlyMeetingStatus(status);
  if (normalizedStatus === 'SUBMITTING') {
    return [
      ['1', 'Uploading the audio capture', true],
      ['2', 'Preparing private processing queue', false],
      ['3', 'Creating summary and action items', false],
    ] as const;
  }
  return [
    ['1', 'Audio capture received', true],
    ['2', 'Private processing in progress', true],
    ['3', 'Creating summary and action items', false],
  ] as const;
};

function ProcessingState({
  meeting,
  isRefreshing,
  onRefresh,
  onRestart,
}: {
  meeting: MeetlyMeeting;
  isRefreshing: boolean;
  onRefresh: () => void;
  onRestart: () => void;
}) {
  const normalizedStatus = normalizeMeetlyMeetingStatus(meeting.status);

  if (normalizedStatus === 'FAILED') {
    return (
      <section
        role="alert"
        className="gt-card mx-auto max-w-2xl overflow-hidden text-center"
        style={{ borderColor: 'rgba(220,38,38,0.22)' }}
      >
        <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-red-50 text-red-700"><RotateCcw size={21} aria-hidden="true" /></div>
        <h2 className="gt-display mt-4 text-xl text-[var(--gt-text-primary)]">Processing stopped</h2>
        <p className="mt-2 text-sm leading-6 text-[var(--gt-text-secondary)]">Meetly could not finish this meeting. Review the source and try again.</p>
        <p className="mt-4 text-xs text-[var(--gt-text-tertiary)]">Most failures are recoverable by capturing the meeting again.</p>
        <div className="mt-6 flex flex-wrap justify-center gap-3">
          <button
            type="button"
            onClick={onRefresh}
            disabled={isRefreshing}
            className="inline-flex min-h-10 items-center gap-2 rounded-full border border-[var(--gt-border-strong)] bg-white px-5 py-2 text-sm font-semibold text-[var(--gt-text-primary)] hover:bg-[var(--gt-surface-1)] disabled:cursor-wait disabled:opacity-60"
          >
            <RefreshCw className={isRefreshing ? 'animate-spin motion-reduce:animate-none' : undefined} size={14} aria-hidden="true" />
            {isRefreshing ? 'Checking…' : 'Re-check status'}
          </button>
          <button
            type="button"
            onClick={onRestart}
            className="inline-flex min-h-10 items-center gap-2 rounded-full bg-[var(--gt-primary)] px-5 py-2 text-sm font-bold text-white"
          >
            Start new recording
          </button>
        </div>
      </section>
    );
  }

  const submitting = normalizedStatus === 'SUBMITTING';
  const steps = stepFor(meeting.status);
  return (
    <section className="gt-card mx-auto max-w-2xl overflow-hidden" aria-live="polite">
      <div className="border-b border-[var(--gt-divider)] bg-[var(--gt-surface-1)] px-6 py-4">
        <span className="inline-flex items-center gap-2 font-mono text-xs font-medium uppercase tracking-[0.16em] text-[var(--gt-text-secondary)]">
          <span className="h-2 w-2 animate-pulse rounded-full bg-[#e66a52] motion-reduce:animate-none" />
          {normalizedStatus === 'SUBMITTING' ? 'Submitting' : 'Processing'}
        </span>
      </div>
      <div className="p-7 sm:p-10">
        <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-full border border-[var(--gt-accent-soft)] bg-[var(--gt-primary-container)] text-[var(--gt-primary)]">
          {submitting ? <LockKeyhole size={26} aria-hidden="true" /> : <Sparkles size={26} aria-hidden="true" />}
        </div>
        <h2 className="gt-display mt-5 text-center text-2xl text-[var(--gt-text-primary)]">
          {submitting ? 'Uploading privately' : 'Private processing'}
        </h2>
        <p className="mx-auto mt-3 max-w-md text-center text-sm leading-6 text-[var(--gt-text-secondary)]">
          {submitting
            ? 'Keep this page open while TeamLio uploads the meeting file.'
            : 'Meetly has submitted the file and is processing it. You can leave this page and return when it is ready.'}
        </p>
        <div
          role="status"
          aria-label={submitting ? 'Uploading meeting' : 'Processing meeting'}
          className="mx-auto mt-5 inline-flex items-center gap-2 rounded-full bg-[var(--gt-primary-container)] px-4 py-2 text-xs font-semibold text-[var(--gt-primary)]"
        >
          <LoaderCircle className="animate-spin motion-reduce:animate-none" size={15} aria-hidden="true" />
          {submitting ? 'Uploading securely…' : 'Working on your meeting…'}
        </div>
        <div className="mx-auto mt-5 flex justify-center">
          <button type="button" onClick={onRefresh} disabled={isRefreshing} className="inline-flex min-h-9 items-center gap-2 rounded-full border border-[var(--gt-border-strong)] bg-white px-4 py-2 text-xs font-semibold text-[var(--gt-text-secondary)] hover:bg-[var(--gt-surface-1)] disabled:cursor-wait disabled:opacity-60">
            <RefreshCw className={isRefreshing ? 'animate-spin motion-reduce:animate-none' : undefined} size={13} aria-hidden="true" />
            {isRefreshing ? 'Checking…' : 'Re-check status'}
          </button>
        </div>

        <ol className="mx-auto mt-8 max-w-lg space-y-3">
          {steps.map(([number, label, active]) => (
            <li key={String(number)} className={`flex items-center gap-3 rounded-2xl border px-4 py-3 ${active ? 'border-[var(--gt-accent-soft)] bg-[var(--gt-primary-container)]' : 'border-[var(--gt-border)] bg-[var(--gt-surface-1)]'}`}>
              <span className="flex h-7 w-7 items-center justify-center rounded-full bg-white font-mono text-xs font-semibold text-[var(--gt-primary)]">{number}</span>
              <span className="text-sm font-semibold text-[var(--gt-text-primary)]">{label}</span>
            </li>
          ))}
        </ol>
      </div>
    </section>
  );
}

export default function MeetlyWorkspace() {
  const location = useLocation();
  const navigate = useNavigate();
  const { meetingId } = useParams<{ meetingId: string }>();
  const captureMode = !meetingId && isCaptureRequest(location.search);
  const [meetings, setMeetings] = useState<MeetlyMeeting[]>([]);
  const [landingLoading, setLandingLoading] = useState(!meetingId && !captureMode);
  const [listFailed, setListFailed] = useState(false);
  const [listReloadToken, setListReloadToken] = useState(0);
  const [recording, setRecording] = useState<StoppedRecording | null>(null);
  const [meeting, setMeeting] = useState<MeetlyMeeting | null>(null);
  const [loadedMeetingId, setLoadedMeetingId] = useState<string | null>(null);
  const [loading, setLoading] = useState(Boolean(meetingId));
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [importingAudio, setImportingAudio] = useState(false);
  const [requestError, setRequestError] = useState<string | null>(null);
  const mountedRef = useRef(true);
  const importAudioInputRef = useRef<HTMLInputElement>(null);
  const requestGenerationRef = useRef(0);
  const requestInFlightRef = useRef<number | null>(null);
  const pendingMeetingGenerationRef = useRef<number | null>(null);
  const pendingTerminalRefreshRef = useRef<number | null>(null);
  const terminalRefreshRequestedRef = useRef<number | null>(null);
  const requestMeetingRef = useRef<((refreshing: boolean) => Promise<void>) | null>(null);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  // Only the landing view (no meeting id, not capturing) renders a list, so
  // that's the only case that needs to fetch one.
  useEffect(() => {
    if (captureMode || meetingId) {
      setLandingLoading(false);
      return undefined;
    }

    let cancelled = false;
    setLandingLoading(true);

    const loadMeetings = async () => {
      try {
        const response = await meetlyApi.listMeetings({ page: 1, page_size: 50 });
        if (cancelled) return;
        setMeetings(response.data.meetings || []);
        setListFailed(false);
      } catch {
        // Previously this fabricated a placeholder meeting, which made a failed
        // request look like an empty but working workspace.
        if (!cancelled) {
          setMeetings([]);
          setListFailed(true);
        }
      } finally {
        if (!cancelled) setLandingLoading(false);
      }
    };

    void loadMeetings();
    return () => {
      cancelled = true;
    };
  }, [captureMode, meetingId, listReloadToken]);

  const hasMeetings = meetings.length > 0;

  const reloadMeetings = useCallback(() => setListReloadToken(token => token + 1), []);

  const reloadMeetingAndList = useCallback(() => {
    reloadMeetings();
    void requestMeetingRef.current?.(false);
  }, [reloadMeetings]);

  const updateMeeting = useCallback(async (id: string, update: Parameters<typeof meetlyApi.updateMeeting>[1]) => {
    await meetlyApi.updateMeeting(id, update);
    if (mountedRef.current) reloadMeetings();
  }, [reloadMeetings]);

  const deleteMeeting = useCallback(async (id: string) => {
    await meetlyApi.deleteMeeting(id);
    if (mountedRef.current) reloadMeetings();
  }, [reloadMeetings]);

  useEffect(() => {
    setMeeting(null);
    setLoadedMeetingId(null);
    setRequestError(null);
    setIsRefreshing(false);
    if (!meetingId) {
      setLoading(false);
      requestMeetingRef.current = null;
      pendingMeetingGenerationRef.current = null;
      pendingTerminalRefreshRef.current = null;
      terminalRefreshRequestedRef.current = null;
      return undefined;
    }
    // Lio-Notes owns the current Meetly data. Old Teamlio's UUID links cannot
    // exist there, so return visitors to the current meeting list instead of
    // showing a misleading connection error.
    if (!meetingId.startsWith('meeting-')) {
      setLoading(false);
      requestMeetingRef.current = null;
      pendingMeetingGenerationRef.current = null;
      pendingTerminalRefreshRef.current = null;
      terminalRefreshRequestedRef.current = null;
      navigate('/meetly', { replace: true });
      return undefined;
    }
    setLoading(true);
    let disposed = false;
    let pendingRefreshTimer: number | null = null;
    const requestGeneration = ++requestGenerationRef.current;
    // An unresolved initial request is eligible for a terminal event. Once a
    // response is known terminal this generation is removed from eligibility.
    pendingMeetingGenerationRef.current = requestGeneration;

    const clearPendingRefresh = () => {
      if (pendingRefreshTimer === null) return;
      window.clearTimeout(pendingRefreshTimer);
      pendingRefreshTimer = null;
    };

    const schedulePendingRefresh = () => {
      if (disposed
        || requestGeneration !== requestGenerationRef.current
        || pendingRefreshTimer !== null) return;
      pendingRefreshTimer = window.setTimeout(() => {
        pendingRefreshTimer = null;
        void requestMeeting(false);
      }, PENDING_MEETING_REFRESH_MS);
    };

    const requestMeeting = async (refreshing: boolean) => {
      if (disposed
        || requestGeneration !== requestGenerationRef.current
        || requestInFlightRef.current === requestGeneration) return;
      requestInFlightRef.current = requestGeneration;
      if (refreshing) setIsRefreshing(true);
      try {
        const response = await meetlyApi.getMeeting(meetingId);
        if (disposed || requestGeneration !== requestGenerationRef.current) return;
        setMeeting(response.data.meeting);
        if (isPending(response.data.meeting.status)) {
          pendingMeetingGenerationRef.current = requestGeneration;
          schedulePendingRefresh();
        } else if (pendingMeetingGenerationRef.current === requestGeneration) {
          pendingMeetingGenerationRef.current = null;
          clearPendingRefresh();
        }
        setLoadedMeetingId(meetingId);
        setRequestError(null);
        setLoading(false);
      } catch (error) {
        if (disposed || requestGeneration !== requestGenerationRef.current) return;
        setRequestError('Meetly could not load this meeting. Check your connection and try again.');
        setLoading(false);
        const status = (error as { response?: { status?: number } })?.response?.status;
        if (pendingMeetingGenerationRef.current === requestGeneration && status !== 403 && status !== 404) {
          schedulePendingRefresh();
        }
      } finally {
        if (requestInFlightRef.current === requestGeneration) {
          requestInFlightRef.current = null;
        }
        if (!disposed
          && requestGeneration === requestGenerationRef.current
          && pendingTerminalRefreshRef.current === requestGeneration) {
          pendingTerminalRefreshRef.current = null;
          void requestMeeting(true);
        } else if (!disposed && requestGeneration === requestGenerationRef.current) {
          setIsRefreshing(false);
        }
      }
    };

    requestMeetingRef.current = requestMeeting;
    const handleStatusChange = (event: Event) => {
      const detail = (event as CustomEvent<{ meetingId?: string; status?: string }>).detail;
      if (detail?.meetingId !== meetingId
        || (detail.status !== 'COMPLETED' && detail.status !== 'FAILED')
        || requestGeneration !== requestGenerationRef.current
        || pendingMeetingGenerationRef.current !== requestGeneration
        || terminalRefreshRequestedRef.current === requestGeneration) return;

      terminalRefreshRequestedRef.current = requestGeneration;
      if (requestInFlightRef.current === requestGeneration) {
        pendingTerminalRefreshRef.current = requestGeneration;
        setIsRefreshing(true);
        return;
      }
      void requestMeeting(true);
    };

    window.addEventListener('meetly-status-changed', handleStatusChange);
    // A microtask lets StrictMode's probe cleanup cancel its provisional effect
    // before network work begins, without introducing timer-based polling.
    void Promise.resolve().then(() => requestMeeting(false));
    return () => {
      disposed = true;
      window.removeEventListener('meetly-status-changed', handleStatusChange);
      if (requestMeetingRef.current === requestMeeting) requestMeetingRef.current = null;
      if (requestInFlightRef.current === requestGeneration) requestInFlightRef.current = null;
      if (pendingMeetingGenerationRef.current === requestGeneration) pendingMeetingGenerationRef.current = null;
      if (pendingTerminalRefreshRef.current === requestGeneration) pendingTerminalRefreshRef.current = null;
      if (terminalRefreshRequestedRef.current === requestGeneration) terminalRefreshRequestedRef.current = null;
      clearPendingRefresh();
      if (requestGeneration === requestGenerationRef.current) {
        requestGenerationRef.current += 1;
      }
    };
  }, [meetingId, navigate]);

  const processMeeting = async () => {
    if (!recording || submitting) return;
    setSubmitting(true);
    setRequestError(null);
    try {
      const response = await meetlyApi.createMeeting(recording.file, {
        language: 'en',
        summary_template: 'project_sync',
        duration_seconds: recording.durationSeconds,
        capture_mode: recording.captureMode,
      });
      if (!mountedRef.current) return;
      setRecording(null);
      navigate(`/meetly/meetings/${response.data.meeting_id}`);
    } catch (error) {
      if (!mountedRef.current) return;
      setRequestError(submissionErrorMessage(error));
    } finally {
      if (mountedRef.current) setSubmitting(false);
    }
  };

  const importAudio = async (file: File) => {
    if (importingAudio) return;
    setImportingAudio(true);
    setRequestError(null);
    try {
      const response = await meetlyApi.createMeeting(file, {
        language: 'en',
        summary_template: 'project_sync',
        capture_mode: 'imported',
      });
      if (!mountedRef.current) return;
      navigate(`/meetly/meetings/${response.data.meeting_id}`);
    } catch (error) {
      if (mountedRef.current) setRequestError(submissionErrorMessage(error));
    } finally {
      if (mountedRef.current) setImportingAudio(false);
    }
  };

  const currentMeeting = loadedMeetingId === meetingId ? meeting : null;

  const isLanding = !meetingId && !captureMode;
  const showNewRecordingButton = isLanding && !landingLoading && !listFailed && hasMeetings;

  return (
    <main className="gt-shell min-h-screen">
      <div className="mx-auto w-full max-w-[1700px] p-4 md:p-7">
        {isLanding && (
          <header className="mb-7 flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
            <div>
              <p className="gt-eyebrow">Task management / Meetly</p>
              <h1 className="gt-display mt-1 text-3xl text-[var(--gt-text-primary)]">Meeting workspace</h1>
              <p className="mt-2 text-sm text-[var(--gt-text-secondary)]">Capture conversations, review summaries, and turn decisions into tasks.</p>
            </div>
            {showNewRecordingButton && (
              <div className="flex flex-wrap items-center gap-3 self-start sm:self-auto">
                <button type="button" onClick={() => navigate('/meetly/usage')} className="inline-flex min-h-10 shrink-0 items-center gap-2 rounded-full border border-[var(--gt-border-strong)] bg-white px-4 py-2 text-sm font-semibold text-[var(--gt-text-primary)] transition hover:bg-[var(--gt-surface-1)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--gt-primary)] focus-visible:ring-offset-2"><Settings size={16} aria-hidden="true" /> Usage & limits</button>
                <button type="button" onClick={() => navigate('/meetly?record=1')} className="inline-flex min-h-10 shrink-0 items-center gap-2 rounded-full border border-[var(--gt-border-strong)] bg-white px-4 py-2 text-sm font-semibold text-[var(--gt-text-primary)] transition hover:bg-[var(--gt-surface-1)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--gt-primary)] focus-visible:ring-offset-2"><FolderOpen size={16} aria-hidden="true" /> Recording</button>
                <button type="button" onClick={() => importAudioInputRef.current?.click()} disabled={importingAudio} className="inline-flex min-h-10 shrink-0 items-center gap-2 rounded-full border border-[var(--gt-border-strong)] bg-white px-4 py-2 text-sm font-semibold text-[var(--gt-text-primary)] transition hover:bg-[var(--gt-surface-1)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--gt-primary)] focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-60"><FileAudio size={16} aria-hidden="true" /> {importingAudio ? 'Uploading…' : 'Upload audio'}</button>
                <button type="button" onClick={() => navigate('/meetly?record=1')} className="inline-flex min-h-10 shrink-0 items-center gap-2 rounded-full border border-[var(--gt-border-strong)] bg-white px-4 py-2 text-sm font-semibold text-[var(--gt-text-primary)] transition hover:bg-[var(--gt-surface-1)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--gt-primary)] focus-visible:ring-offset-2"><ArrowLeft size={16} aria-hidden="true" /> New recording</button>
              </div>
            )}
          </header>
        )}

        {isLanding && requestError && <div role="alert" className="mb-5 rounded-2xl border border-red-200 bg-red-50 px-5 py-4 text-sm text-red-800">{requestError}</div>}
        {isLanding && (
          <input
            ref={importAudioInputRef}
            type="file"
            accept="audio/*,video/*"
            className="hidden"
            aria-label="Import audio file"
            onChange={event => {
              const file = event.target.files?.[0];
              event.target.value = '';
              if (file) void importAudio(file);
            }}
          />
        )}

        {isLanding ? (
          landingLoading ? (
            <section className="gt-card mx-auto flex min-h-[300px] max-w-2xl flex-col items-center justify-center p-8 text-center" aria-live="polite">
              <Clock3 className="text-[var(--gt-primary)]" size={28} aria-hidden="true" />
              <p className="gt-display mt-4 text-lg text-[var(--gt-text-primary)]">Opening private workspace…</p>
            </section>
          ) : listFailed ? (
            <section role="alert" className="gt-card mx-auto flex min-h-[300px] max-w-2xl flex-col items-center justify-center p-8 text-center">
              <span className="flex h-12 w-12 items-center justify-center rounded-full bg-red-50 text-red-700">
                <RotateCcw size={21} aria-hidden="true" />
              </span>
              <h2 className="gt-display mt-4 text-xl text-[var(--gt-text-primary)]">Could not load meetings</h2>
              <p className="mt-2 max-w-md text-sm leading-6 text-[var(--gt-text-secondary)]">
                Meetly could not reach the meeting service. Check your connection and try again.
              </p>
              <button
                type="button"
                onClick={reloadMeetings}
                className="mt-5 inline-flex min-h-11 cursor-pointer items-center justify-center rounded-full bg-[var(--gt-primary)] px-5 py-2 text-sm font-bold text-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--gt-primary)] focus-visible:ring-offset-2"
              >
                Try again
              </button>
            </section>
          ) : !hasMeetings ? (
            <section className="gt-card mx-auto flex min-h-[300px] max-w-2xl flex-col items-center justify-center p-8 text-center">
              <span className="flex h-14 w-14 items-center justify-center rounded-full border border-[var(--gt-accent-soft)] bg-[var(--gt-primary-container)] text-[var(--gt-primary)]">
                <Sparkles size={23} aria-hidden="true" />
              </span>
              <h2 className="gt-display mt-4 text-xl text-[var(--gt-text-primary)]">No meetings yet</h2>
              <p className="mt-2 max-w-md text-sm leading-6 text-[var(--gt-text-secondary)]">
                Record a meeting or upload an audio file. Meetly will turn it into a summary and action items.
              </p>
              <div className="mt-5 flex flex-wrap justify-center gap-3">
                <button
                  type="button"
                  onClick={() => navigate('/meetly?record=1')}
                  className="inline-flex min-h-11 cursor-pointer items-center justify-center gap-2 rounded-full bg-[var(--gt-primary)] px-5 py-2 text-sm font-bold text-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--gt-primary)] focus-visible:ring-offset-2"
                >
                  <Mic size={16} aria-hidden="true" /> Start Recording
                </button>
                <button
                  type="button"
                  onClick={() => importAudioInputRef.current?.click()}
                  disabled={importingAudio}
                  className="inline-flex min-h-11 cursor-pointer items-center justify-center gap-2 rounded-full border border-[var(--gt-border-strong)] bg-white px-5 py-2 text-sm font-bold text-[var(--gt-text-primary)] transition hover:bg-[var(--gt-surface-1)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--gt-primary)] focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-60"
                >
                  <FileAudio size={16} aria-hidden="true" /> {importingAudio ? 'Uploading…' : 'Upload audio'}
                </button>
              </div>
            </section>
          ) : (
            <MeetlyMeetingList
              meetings={meetings}
              onSelect={id => navigate(`/meetly/meetings/${encodeURIComponent(id)}`)}
              onUpdate={updateMeeting}
              onDelete={deleteMeeting}
            />
          )
        ) : (
          <>
            {requestError && <div role="alert" className="mb-5 rounded-2xl border border-red-200 bg-red-50 px-5 py-4 text-sm text-red-800">{requestError}</div>}

            {meetingId ? (
              loading ? (
                <section className="gt-card mx-auto flex min-h-[300px] max-w-2xl flex-col items-center justify-center p-8 text-center" aria-live="polite">
                  <Clock3 className="text-[var(--gt-primary)]" size={28} aria-hidden="true" />
                  <p className="gt-display mt-4 text-lg text-[var(--gt-text-primary)]">Opening private workspace…</p>
                </section>
              ) : currentMeeting ? (
                  normalizeMeetlyMeetingStatus(currentMeeting.status) === 'COMPLETED'
                  ? (
                    <MeetlyResultWorkspace
                      meeting={currentMeeting}
                      onMeetingChanged={reloadMeetingAndList}
                    />
                  )
                  : (
                    <ProcessingState
                      meeting={currentMeeting}
                      isRefreshing={isRefreshing}
                      onRestart={() => navigate('/meetly?record=1')}
                      onRefresh={() => void requestMeetingRef.current?.(true)}
                    />
                  )
              ) : null
            ) : (
              <>
                <MeetlyRecorder onRecordingReady={setRecording} onRecordingInvalidated={() => setRecording(null)} disabled={submitting} />
                {recording && (
                  <section className="mt-5 flex flex-col gap-4 rounded-[20px] border border-[var(--gt-accent-soft)] bg-[var(--gt-primary-container)] p-5 sm:flex-row sm:items-center sm:justify-between" aria-live="polite">
                    <div>
                      <p className="text-sm font-bold text-[var(--gt-primary)]">Capture ready</p>
                      <p className="mt-1 text-xs leading-5 text-[var(--gt-text-secondary)]">Process it privately to generate the summary and action items.</p>
                    </div>
                    <button type="button" onClick={() => void processMeeting()} disabled={submitting} className="inline-flex min-h-11 shrink-0 items-center justify-center gap-2 rounded-full bg-[var(--gt-primary)] px-6 py-3 text-sm font-bold text-white transition hover:bg-[var(--gt-primary-hover)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--gt-primary)] focus-visible:ring-offset-2 disabled:cursor-wait disabled:opacity-60">
                      <Sparkles size={17} aria-hidden="true" /> {submitting ? 'Submitting…' : 'Process meeting'}
                    </button>
                  </section>
                )}
              </>
            )}
          </>
        )}
      </div>
    </main>
  );
}
